import { MutableRefObject, useCallback, useRef } from "react";
import { AgChartInstance } from "ag-charts-community";

interface UseZoomableChartRefOptions {
  onDownload?: () => void;
}

interface UseZoomableChartRefResult {
  chartRef: MutableRefObject<AgChartInstance | null>;
  downloadChart: () => void;
  onOpenZoom: () => void;
  onCloseZoom: () => void;
}

/**
 * Manages the chart ref lifecycle for a chart rendered inside a `ZoomableGridCard`.
 * The card unmounts/remounts the chart when zooming, so the ref is stashed on open
 * and restored on close to avoid losing the instance (e.g. for `.download()`).
 */
export function useZoomableChartRef({ onDownload }: UseZoomableChartRefOptions = {}): UseZoomableChartRefResult {
  const chartRef = useRef<AgChartInstance | null>(null);
  const previousChartRef = useRef<AgChartInstance | null>(null);

  const downloadChart = useCallback(() => {
    if (!chartRef.current) return;
    chartRef.current.download();
    onDownload?.();
  }, [onDownload]);

  const onOpenZoom = useCallback(() => {
    previousChartRef.current = chartRef.current;
  }, []);

  const onCloseZoom = useCallback(() => {
    chartRef.current = previousChartRef.current;
  }, []);

  return { chartRef, downloadChart, onOpenZoom, onCloseZoom };
}
